"use client";
import React, { useEffect, useState } from "react";
import "@/styles/components/privacy_policy.scss";

import Navbar from "@/components/common/Navbar";
import Footer from "@/components/common/Footer";
import Image from "next/image";

const termsData = [
  {
    id: "refund-cancellation",
    title: "Refund & Cancellation Policy",
    items: [
      {
        id: "refund-eligibility",
        title: "Eligibility for Refunds",
        content: [
          "We strive to ensure that every order arrives in the condition and specification agreed at the time of purchase. A refund may be approved under the following circumstances:",
          [
            "Damaged Items – The product arrives physically damaged.",
            "Defective Items – The product does not function as intended upon arrival.",
            "Missing Items – The shipment is incomplete or items are missing.",
            "Incorrect Items – The delivered product does not match the item ordered.",
          ],
          "To process a claim, customers may be required to provide supporting evidence such as photos, videos, or detailed order information.",
        ],
      },
      {
        id: "not-eligible-refund",
        title: "Situations Not Eligible for Refund",
        content: [
          "Refunds or cancellations will not be approved in the following cases:",
          [
            "The item was delivered within a reasonable timeframe and matches the product description.",
            "The customer changes their mind after the order has been prepared for export or shipped from the source country.",
            "The issue results from incorrect information provided by the customer (e.g., wrong shipping address or contact details).",
            "The reason for return relates to normal wear and tear, minor cosmetic variations, or color differences caused by screen display settings.",
          ],
        ],
      },
      {
        id: "cancellation-policy",
        title: "Cancellation Policy",
        content: [
          "Orders may be cancelled only before they are processed or shipped.",
          "Once an order has entered fulfillment, export preparation, or transit, cancellation is no longer possible unless the item qualifies under the refund eligibility criteria stated above.",
        ],
      },
      {
        id: "refund-method",
        title: "Refund Method",
        content: [
          "If a refund is approved, it will be issued using one of the following methods:",
          [
            "E-Wallet Credit (Default Method) – Refunds are automatically credited to the customer’s e-wallet. Funds are available immediately for future purchases. This method avoids banking delays and ensures the fastest resolution.",
            "Original Payment Method (Subject to Payment Provider Support) – Customers may request a refund to their original payment method (e.g., credit card, debit card, PayPal). Processing typically takes 3–5 business days depending on the payment provider and financial institution. Once processed by us, any additional delays are outside our control.",
          ],
        ],
      },
      {
        id: "shipping-costs",
        title: "Shipping Costs",
        content: [
          "If the refund is approved due to damage, defect, or an error on our part, shipping costs will be fully covered.",
          "If the return is requested for reasons not covered under the eligibility criteria, shipping costs are non-refundable.",
        ],
      },
      {
        id: "fraud-prevention",
        title: "Fraud Prevention & Policy Abuse",
        content: [
          "We reserve the right to decline refund requests that show signs of misuse, repeated claims without sufficient evidence, or activity inconsistent with normal customer behavior.",
        ],
      },
    ],
  },
];

export default function TermsOfUse() {
  const [active, setActive] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      termsData.forEach((section) => {
        section.items.forEach((item) => {
          const element = document.getElementById(item.id);
          if (!element) return;

          const top = element.offsetTop;
          const bottom = top + element.offsetHeight;

          if (scrollPosition >= top && scrollPosition < bottom) {
            setActive(item.id);
          }
        });
      });
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const renderContent = (content) => {
    if (typeof content === "string") return <p>{content}</p>;

    if (Array.isArray(content)) {
      return content.map((item, index) => {
        if (Array.isArray(item)) {
          return (
            <ul key={index}>
              {item.map((li, i) => (
                <li key={i}>{li}</li>
              ))}
            </ul>
          );
        }
        return <p key={index}>{item}</p>;
      });
    }
  };

  return (
    <>
      <div className="section navbar_section">
        <Navbar />
      </div>

      {/* HEADER */}
      <section id="policy_header">
        <h1>Refund Policy</h1>

        <div className="box_image">
          <Image
            src="/images/return-policy.png"
            width={422}
            height={282}
            alt="box"
            className="terms"
          />
        </div>
      </section>

      {/* CONTENT */}
      <section id="policy_section">
        <div className="policy_wrapper">
          {/* SIDEBAR */}
          <div className="policy_sidebar">
            <ul>
              {termsData.map((section) =>
                section.items.map((item, itemIndex) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className={active === item.id ? "active" : ""}
                    >
                      {item.title}
                    </a>
                  </li>
                )),
              )}
            </ul>
          </div>
          {/* CONTENT */}
          <div className="policy_content">
            {termsData.map((section) =>
              section.items.map((item, itemIndex) => (
                <section key={item.id} id={item.id}>
                  <h2>
                    {itemIndex + 1}. {item.title}
                  </h2>

                  {renderContent(item.content)}
                </section>
              )),
            )}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
